import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

// import { useRouter } from 'next/router';
import { SyntheticEvent, useCallback, useState } from 'react';
import { useMutation } from 'react-query';

import { useLoadingStore } from '@stores/useLoadingStore';
import { useReadStoryStore } from '@stores/useReadStoryStore';
import { FontSizes, Sizes, Spacing } from 'chatie-design-system';
import { ArrowRight, CheckCircle, Icon } from 'chatie-design-system/icon';

import { ActionSheetOption, KebabMenu } from '@components/common/ActionSheet';
import Story, { StoryProps, storyActionCSS } from '@components/stories/Story';

import { deleteReadStory, fetchStory } from '@api/story';
import { EpisodeReadFrom, Platform, QUERY_KEYS, StoryTheme } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { useAnalytics } from '@utils/analytics';
import { toQueryString } from '@utils/misc';
import { useOptimisticUpdate } from '@utils/query';
import { useWebView } from '@utils/webview';

const readStoryCSS = css`
  position: relative;

  > article {
    max-width: 85%;
  }

  button {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    padding: ${Spacing.medium}px;
    font-size: ${FontSizes.xsmall}px;
  }
`;
const storySelecButtonCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(-50%);
  color: ${theme.var.black};
  top: 50%;
`;

type LikedStoryAction = 'unlike';

type ReadStoryProps = {
  isEdit: boolean;
} & StoryProps;

const ReadStory = (props: ReadStoryProps) => {
  const { t } = useTranslation();
  const { id, isEdit } = props;

  const { platform } = useGlobalContext();
  const theme = useTheme();
  const router = useRouter();
  const { firebase } = useAnalytics();
  const { web } = useWebView();
  const { setLoading } = useLoadingStore();

  const { selectedIds, select, cancelSelect } = useReadStoryStore();
  const isCheck = selectedIds.includes(id);

  const [actions] = useState<ActionSheetOption<LikedStoryAction>[]>([
    { label: t('삭제하기'), value: 'unlike', isDanger: true },
  ]);

  const optimistic = useOptimisticUpdate();
  const { mutate } = useMutation((id: number) => deleteReadStory(id));

  const handleAction = useCallback(
    (action: LikedStoryAction) => {
      if (action === 'unlike') {
        setLoading(true);
        mutate(id, {
          onSuccess: () => {
            optimistic.remove(QUERY_KEYS.READ_STORY.LIST('latest'), id);
            optimistic.remove(QUERY_KEYS.READ_STORY.LIST('update'), id);
          },
          onSettled: () => setLoading(false),
        });
      }
    },
    [id]
  );

  const handleContinueRead = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();

      const run = async () => {
        const story = await fetchStory(id);
        if (!story.latestRead) {
          return;
        }
        if (story.theme === StoryTheme.iknow) {
          if (platform === Platform.web) {
            router.push(
              `/stories/${story.id}/episodes/${
                story.latestRead.episodeId
              }/licensed${toQueryString({ readFrom: EpisodeReadFrom.list })}`
            );
          } else {
            web.openPage(
              `/stories/${story.id}/episodes/${
                story.latestRead.episodeId
              }/licensed${toQueryString({ readFrom: EpisodeReadFrom.list })}`
            );
          }
          return;
        }

        const url = `/to${toQueryString({
          url: `/stories/${story.id}/episodes/${
            story.latestRead.episodeId
          }${toQueryString({ readFrom: EpisodeReadFrom.list })}`,
        })}`;
        firebase.logEvent('continue_read', { storyId: story.id });
        if (platform === Platform.web) {
          router.push(url);
        } else {
          web.openPage(url);
        }
      };

      run().catch(() => {
        alert(
          t(
            '이어보기 정보를 불러오는데 문제가 발생했습니다. 잠시 후 다시 시도해주세요.'
          )
        );
      });
    },
    [id]
  );

  const handleSelectStory = (e: SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();

    const selectedId = Number(e.currentTarget.dataset.id);

    if (isCheck) {
      cancelSelect(selectedId);
    } else {
      select(selectedId);
    }
  };

  return (
    <article
      css={readStoryCSS}
      onClick={isEdit ? handleSelectStory : undefined}
      data-id={id}
    >
      <Story {...props} tapCount={undefined} isLibrary isInflow={!isEdit} />
      {isEdit ? (
        <button css={storySelecButtonCSS} data-id={id}>
          <Icon
            icon={CheckCircle}
            size={Sizes.large}
            color={isCheck ? theme.colors.main500 : theme.var.gray200}
          />
        </button>
      ) : (
        <>
          <button className="icon-text" onClick={handleContinueRead}>
            <b>{t('이어보기')}</b>
            <Icon icon={ArrowRight} size={Sizes.xsmall} />
          </button>
          <KebabMenu
            css={storyActionCSS}
            name={`read-story-${id}`}
            options={actions}
            onSelect={handleAction}
          />
        </>
      )}
    </article>
  );
};

export default ReadStory;
